import { useState, useCallback, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getComments, addComment, likeComment, updateComment } from '../queries/comments';
import { useCommentsStore } from '../stores/commentsStore';

const DEFAULT_LIMIT = 10;

function useComment(targetId: string) {
  const {
    setComments,
    setPaginationInfo,
    setCurrentPage,
    resetComments,
    currentPage,
    totalPages,
  } = useCommentsStore();

  const [isLoadingMore, setIsLoadingMore] = useState(false);

  useEffect(() => {
    resetComments();
  }, [targetId]);

  const {
    data,
    isError,
    error,
    isSuccess,
    isPending,
    refetch,
  } = useQuery({
    queryKey: ['comments', targetId, 1],
    queryFn: () => getComments(targetId, 1, DEFAULT_LIMIT),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: !!targetId,
  });

  useEffect(() => {
    if (isSuccess && data) {
      const comments = data.data || data;
      const total = data.pagination?.total ?? comments.length;
      const pages = data.pagination?.totalPages ?? 1;

      setComments(comments, true);
      setPaginationInfo(total, pages);
      setCurrentPage(1);
    }
  }, [isSuccess, data]);

  const fetchMoreComments = useCallback(async () => {
    if (currentPage >= totalPages || isLoadingMore) return;

    const nextPage = currentPage + 1;
    setIsLoadingMore(true);

    try {
      const response = await getComments(targetId, nextPage, DEFAULT_LIMIT);
      const comments = response.data || response;

      setComments(comments, false);
      setCurrentPage(nextPage);
    } catch (e) {
      console.error('Erro ao carregar mais comentários:', e);
    } finally {
      setIsLoadingMore(false);
    }
  }, [targetId, currentPage, totalPages, isLoadingMore, setComments, setCurrentPage]);

  return {
    data,
    isError,
    isLoading: isPending,
    isLoadingMore,
    error,
    isSuccess,
    addComment,
    likeComment,
    updateComment,
    fetchMoreComments,
    refetch,
  };
}

export default useComment;
